import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import type { Dependency, WorkItemDetail } from '../lib/types';
import {
  DEPENDENCY_TONE,
  PRIORITY_TONE,
  READINESS_LABEL,
  READINESS_TONE,
  WAITING_STATUS_LABEL,
  WAITING_STATUS_TONE,
  WORK_ITEM_STATUS_TONE,
  WORK_ITEM_TYPE_LABEL,
  formatDate,
  formatDateTime,
  relativeDays,
  titleCase,
  toDateTimeInput,
} from '../lib/format';
import {
  Badge,
  Button,
  Callout,
  Detail,
  Dialog,
  ErrorNotice,
  Field,
  Input,
  Loading,
  Select,
  Textarea,
  useToast,
} from '../components/ui';

/**
 * Work item detail.
 *
 * For a commitment this is where §9's three dates, §22's escalation state and
 * §8's dependency chain are all visible at once — which is the point: a promise
 * is only trustworthy if you can see what it depends on and when the internal
 * runway actually starts.
 */
export function WorkItemDialog({
  workItemId,
  onClose,
  onChanged,
}: {
  workItemId: string;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [mode, setMode] = useState<'view' | 'complete' | 'reopen' | 'dependency' | 'reschedule'>(
    'view',
  );

  const query = useQuery({
    queryKey: ['work-item', workItemId],
    queryFn: () => api.get<WorkItemDetail>(`/work-items/${workItemId}`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['work-item', workItemId] });
    void queryClient.invalidateQueries({ queryKey: ['my-work'] });
    void queryClient.invalidateQueries({ queryKey: ['work-items'] });
    void queryClient.invalidateQueries({ queryKey: ['leadership'] });
    void queryClient.invalidateQueries({ queryKey: ['account-summary'] });
    onChanged?.();
  };

  const detail = query.data;
  const item = detail?.workItem;

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={item?.title ?? 'Work item'}
      description={
        item ? (
          <span className="flex flex-wrap items-center gap-2">
            <Badge dot={false}>{WORK_ITEM_TYPE_LABEL[item.type]}</Badge>
            <Link to={`/accounts/${item.accountId}`} className="text-accent hover:underline">
              {item.accountName}
            </Link>
            {item.projectName && <span className="text-muted">· {item.projectName}</span>}
          </span>
        ) : undefined
      }
      footer={
        item && (
          <>
            {item.status === 'completed' ? (
              <Button variant="secondary" onClick={() => setMode('reopen')}>
                Reopen
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setMode('dependency')}>
                  Add dependency
                </Button>
                {item.commitment && (
                  <Button variant="secondary" onClick={() => setMode('reschedule')}>
                    Change dates
                  </Button>
                )}
                <Button variant="primary" onClick={() => setMode('complete')}>
                  Mark complete
                </Button>
              </>
            )}
          </>
        )
      }
    >
      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}

      {detail && item && (
        <div className="space-y-5">
          {/* §10 — the review prompt, never an automatic close */}
          {detail.followupPrompt && (
            <Callout tone="info" title={detail.followupPrompt}>
              <p>
                A customer message appears to satisfy this request. Automation flagged it; only you
                can close it.
              </p>
              <div className="mt-2 flex gap-2">
                <FollowupAction
                  workItemId={item.id}
                  event="resolved"
                  label="Review and close"
                  variant="primary"
                  onDone={() => {
                    toast('Follow-up resolved');
                    invalidate();
                  }}
                />
                <FollowupAction
                  workItemId={item.id}
                  event="reopened"
                  label="Not answered — keep waiting"
                  onDone={() => {
                    toast('Still waiting on the customer', 'warning');
                    invalidate();
                  }}
                />
              </div>
            </Callout>
          )}

          {/* §22 escalation */}
          {item.commitment?.leadershipVisibleAt && (
            <Callout tone="critical" title="Visible to leadership">
              <p>{item.commitment.escalationReason}</p>
              <p className="mt-1 text-muted">
                Escalated {formatDateTime(item.commitment.leadershipVisibleAt)}.
              </p>
            </Callout>
          )}

          {/* §35 override */}
          {item.commitment?.overrideWarningReason && (
            <Callout tone="warning" title="Recorded over a feasibility warning">
              <p>{item.commitment.overrideWarningReason}</p>
              <p className="mt-1 text-muted">
                Product had not confirmed delivery timing when this promise was recorded.
              </p>
            </Callout>
          )}

          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            <Detail label="Status">
              <Badge tone={WORK_ITEM_STATUS_TONE[item.status]}>{titleCase(item.status)}</Badge>
            </Detail>
            <Detail label="Priority">
              <Badge tone={PRIORITY_TONE[item.priority]}>{titleCase(item.priority)}</Badge>
            </Detail>
            <Detail label="Owner">{item.ownerName}</Detail>
            {/* Design 0001 — work is owed to a person, not just an account. */}
            <Detail label="Owed to">
              {detail.owedToContact ? (
                <>
                  <span className="text-ink">{detail.owedToContact.name}</span>
                  {detail.owedToContact.title && (
                    <span className="block text-xs text-muted">{detail.owedToContact.title}</span>
                  )}
                  {detail.owedToContact.status !== 'active' && (
                    <Badge tone="warning" className="mt-1">
                      {titleCase(detail.owedToContact.status)}
                    </Badge>
                  )}
                </>
              ) : (
                <span className="text-muted">No customer recipient</span>
              )}
            </Detail>
            {detail.informedContacts.length > 0 && (
              <Detail label="Informed">
                {detail.informedContacts.map((c) => (
                  <span key={c.id} className="block text-xs text-ink-2">
                    {c.name}
                  </span>
                ))}
              </Detail>
            )}
            <Detail label="Created">
              {item.createdByType === 'ai_proposal'
                ? 'From an AI proposal'
                : item.createdByType === 'system'
                  ? 'By the system'
                  : 'By a person'}
            </Detail>
          </dl>

          {item.description && (
            <div>
              <h3 className="mb-1 text-xs font-medium text-muted">Description</h3>
              <p className="text-sm whitespace-pre-wrap text-ink">{item.description}</p>
            </div>
          )}

          {/* §9 — the three dates, kept separate */}
          {item.commitment && (
            <div className="rounded-lg border border-border bg-surface-2/60 p-3">
              <h3 className="mb-2 flex items-center justify-between text-xs font-semibold text-ink">
                Commitment
                <Badge tone={READINESS_TONE[item.commitment.readiness]}>
                  {READINESS_LABEL[item.commitment.readiness]}
                </Badge>
              </h3>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
                <Detail label="Customer due date">
                  <span className="font-medium">{formatDate(item.commitment.customerDueAt)}</span>
                  <span className="ml-2 text-xs text-muted">
                    {relativeDays(item.commitment.customerDueAt)}
                  </span>
                </Detail>
                <Detail label="Internal target">
                  {formatDate(item.commitment.internalTargetAt)}
                  <span className="mt-0.5 block text-xs text-muted">
                    The runway before the customer date
                  </span>
                </Detail>
                <Detail label="Internal readiness check">
                  {formatDate(item.commitment.internalCheckAt)}
                  <span className="mt-0.5 block text-xs text-muted">
                    When to confirm it will land
                  </span>
                </Detail>
                <Detail label="Category">{titleCase(item.commitment.commitmentCategory)}</Detail>
                <Detail label="Told to the customer?">
                  {item.commitment.externallyCommitted ? (
                    <Badge tone="serious">Externally committed</Badge>
                  ) : (
                    <Badge tone="neutral">Internal only</Badge>
                  )}
                </Detail>
                {detail.escalation && (
                  <Detail label="Escalation threshold">
                    <span className="tnum">
                      {detail.escalation.thresholdBusinessDays} business days overdue
                    </span>
                    <span className="mt-0.5 block text-xs text-muted">
                      Currently {detail.escalation.businessDaysOverdue} overdue
                    </span>
                  </Detail>
                )}
              </dl>
            </div>
          )}

          {item.followup && (
            <div className="rounded-lg border border-border bg-surface-2/60 p-3">
              <h3 className="mb-2 flex items-center justify-between text-xs font-semibold text-ink">
                Waiting on customer
                <Badge tone={WAITING_STATUS_TONE[item.followup.waitingStatus]}>
                  {WAITING_STATUS_LABEL[item.followup.waitingStatus]}
                </Badge>
              </h3>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
                <Detail label="Waiting for">{item.followup.waitingFor}</Detail>
                <Detail label="Customer expected by">
                  {formatDate(item.followup.customerExpectedAt)}
                </Detail>
                <Detail label="Follow up on">
                  {formatDate(item.followup.followupAt)}
                  <span className="ml-2 text-xs text-muted">
                    {relativeDays(item.followup.followupAt)}
                  </span>
                </Detail>
                <Detail label="Last followed up">
                  {formatDate(item.followup.lastFollowedUpAt)}
                </Detail>
              </dl>
              {item.status !== 'completed' && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <FollowupAction
                    workItemId={item.id}
                    event="followed_up"
                    label="I followed up"
                    onDone={() => {
                      toast('Recorded that you followed up');
                      invalidate();
                    }}
                  />
                  <FollowupAction
                    workItemId={item.id}
                    event="resolved"
                    label="Resolved"
                    variant="primary"
                    onDone={() => {
                      toast('Follow-up resolved');
                      invalidate();
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {item.opportunity && (
            <div className="rounded-lg border border-border bg-surface-2/60 p-3">
              <h3 className="mb-2 text-xs font-semibold text-ink">Opportunity signal</h3>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
                <Detail label="Signal">{titleCase(item.opportunity.signalType)}</Detail>
                <Detail label="Potential">{item.opportunity.potentialValueText ?? '—'}</Detail>
                <Detail label="Target period">{item.opportunity.targetPeriod ?? '—'}</Detail>
              </dl>
              <p className="mt-2 text-xs text-muted">
                Not a CRM opportunity — a signal for a person to act on.
              </p>
            </div>
          )}

          {/* §8 dependency chain */}
          <div>
            <h3 className="mb-2 text-xs font-medium text-muted">
              Dependencies ({detail.dependencies.length})
            </h3>
            {detail.dependencies.length === 0 ? (
              <p className="text-xs text-ink-2">
                Nothing recorded. A commitment that depends on support, product, engineering, QA or
                a release should say so — that chain is what makes readiness meaningful.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {detail.dependencies.map((dependency) => (
                  <DependencyRow
                    key={dependency.id}
                    dependency={dependency}
                    onChanged={invalidate}
                  />
                ))}
              </ul>
            )}
          </div>

          {detail.subtasks.length > 0 && (
            <div>
              <h3 className="mb-2 text-xs font-medium text-muted">
                Subtasks ({detail.subtasks.length})
              </h3>
              <ul className="space-y-1">
                {detail.subtasks.map((subtask) => (
                  <li
                    key={subtask.id}
                    className="flex items-center justify-between rounded border border-border px-2.5 py-1.5 text-xs"
                  >
                    <span className="text-ink">{subtask.title}</span>
                    <Badge tone={WORK_ITEM_STATUS_TONE[subtask.status]}>
                      {titleCase(subtask.status)}
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {mode === 'complete' && (
            <CompleteForm
              workItemId={item.id}
              externallyCommitted={Boolean(item.commitment?.externallyCommitted)}
              onCancel={() => setMode('view')}
              onDone={() => {
                toast('Marked complete');
                setMode('view');
                invalidate();
              }}
            />
          )}
          {mode === 'reopen' && (
            <ReopenForm
              workItemId={item.id}
              onCancel={() => setMode('view')}
              onDone={() => {
                toast('Reopened', 'warning');
                setMode('view');
                invalidate();
              }}
            />
          )}
          {mode === 'dependency' && (
            <AddDependencyForm
              workItemId={item.id}
              onCancel={() => setMode('view')}
              onDone={() => {
                toast('Dependency added');
                setMode('view');
                invalidate();
              }}
            />
          )}
          {mode === 'reschedule' && item.commitment && (
            <RescheduleForm
              workItemId={item.id}
              currentDueAt={item.commitment.customerDueAt}
              externallyCommitted={item.commitment.externallyCommitted}
              onCancel={() => setMode('view')}
              onDone={() => {
                toast('Dates updated');
                setMode('view');
                invalidate();
              }}
            />
          )}
        </div>
      )}
    </Dialog>
  );
}

function DependencyRow({
  dependency,
  onChanged,
}: {
  dependency: Dependency;
  onChanged: () => void;
}) {
  const mutation = useMutation({
    mutationFn: (status: string) =>
      api.patch(`/work-items/dependencies/${dependency.id}`, { status }),
    onSuccess: onChanged,
  });

  return (
    <li className="flex flex-wrap items-center justify-between gap-2 rounded border border-border px-2.5 py-2">
      <div className="min-w-0">
        <p className="text-sm text-ink">{dependency.description}</p>
        <p className="mt-0.5 text-xs text-muted">
          {dependency.dependsOnTitle
            ? `Work item: ${dependency.dependsOnTitle}`
            : `${titleCase(dependency.externalDependencyType)}${dependency.externalReference ? ` · ${dependency.externalReference}` : ''}`}
          {dependency.expectedBy && ` · expected ${formatDate(dependency.expectedBy)}`}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Badge tone={DEPENDENCY_TONE[dependency.status]}>{titleCase(dependency.status)}</Badge>
        <Select
          value={dependency.status}
          disabled={mutation.isPending}
          onChange={(event) => mutation.mutate(event.target.value)}
          className="h-7 w-32 py-0 text-xs"
        >
          <option value="pending">Pending</option>
          <option value="in_progress">In progress</option>
          <option value="satisfied">Satisfied</option>
          <option value="blocked">Blocked</option>
        </Select>
      </div>
    </li>
  );
}

function FollowupAction({
  workItemId,
  event,
  label,
  variant = 'secondary',
  onDone,
}: {
  workItemId: string;
  event: string;
  label: string;
  variant?: 'primary' | 'secondary';
  onDone: () => void;
}) {
  const mutation = useMutation({
    mutationFn: () => api.post(`/work-items/${workItemId}/followup/transition`, { event }),
    onSuccess: onDone,
  });
  return (
    <Button
      size="sm"
      variant={variant}
      disabled={mutation.isPending}
      onClick={() => mutation.mutate()}
    >
      {label}
    </Button>
  );
}

function CompleteForm({
  workItemId,
  externallyCommitted,
  onCancel,
  onDone,
}: {
  workItemId: string;
  externallyCommitted: boolean;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState('');
  const mutation = useMutation({
    mutationFn: () => api.post(`/work-items/${workItemId}/complete`, { note: note || undefined }),
    onSuccess: onDone,
  });

  return (
    <InlineForm
      title="Mark complete"
      onCancel={onCancel}
      onSubmit={() => mutation.mutate()}
      pending={mutation.isPending}
      error={mutation.error}
      submitLabel="Complete"
    >
      {externallyCommitted && (
        <Callout tone="warning" title="This was promised to the customer">
          Completing it records that the promise was kept. Only a person can do this — automation
          may never mark an external commitment complete.
        </Callout>
      )}
      <Field label="Note" hint="Optional. Stored on the audit record.">
        <Textarea value={note} onChange={(event) => setNote(event.target.value)} rows={2} />
      </Field>
    </InlineForm>
  );
}

function ReopenForm({
  workItemId,
  onCancel,
  onDone,
}: {
  workItemId: string;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const mutation = useMutation({
    mutationFn: () => api.post(`/work-items/${workItemId}/reopen`, { reason }),
    onSuccess: onDone,
  });

  return (
    <InlineForm
      title="Reopen"
      onCancel={onCancel}
      onSubmit={() => mutation.mutate()}
      pending={mutation.isPending}
      error={mutation.error}
      submitLabel="Reopen"
      disabled={reason.trim().length === 0}
    >
      <Field label="Reason" required hint="Reopening a completed item is audited.">
        <Textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={2} />
      </Field>
    </InlineForm>
  );
}

function AddDependencyForm({
  workItemId,
  onCancel,
  onDone,
}: {
  workItemId: string;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [description, setDescription] = useState('');
  const [type, setType] = useState('gitlab_issue');
  const [reference, setReference] = useState('');
  const [status, setStatus] = useState('pending');
  const [expectedBy, setExpectedBy] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/work-items/${workItemId}/dependencies`, {
        description,
        externalDependencyType: type,
        externalReference: reference || null,
        status,
        expectedBy: expectedBy ? new Date(expectedBy).toISOString() : null,
      }),
    onSuccess: onDone,
  });

  return (
    <InlineForm
      title="Add a dependency"
      onCancel={onCancel}
      onSubmit={() => mutation.mutate()}
      pending={mutation.isPending}
      error={mutation.error}
      submitLabel="Add"
      disabled={description.trim().length === 0}
    >
      <Field label="What is this waiting on?" required>
        <Input
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="e.g. Production release of the revised scoring model"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Type">
          <Select value={type} onChange={(event) => setType(event.target.value)}>
            <option value="support_ticket">Support ticket</option>
            <option value="gitlab_issue">GitLab issue</option>
            <option value="customer">Customer</option>
            <option value="product_item">Product item</option>
            <option value="internal_team">Internal team</option>
            <option value="custom">Other</option>
          </Select>
        </Field>
        <Field label="Reference" hint="Ticket or issue identifier">
          <Input value={reference} onChange={(event) => setReference(event.target.value)} />
        </Field>
        <Field label="Status">
          <Select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="pending">Pending</option>
            <option value="in_progress">In progress</option>
            <option value="satisfied">Satisfied</option>
            <option value="blocked">Blocked</option>
          </Select>
        </Field>
        <Field label="Expected by" hint="Used to judge readiness against the internal target">
          <Input
            type="datetime-local"
            value={expectedBy}
            onChange={(event) => setExpectedBy(event.target.value)}
          />
        </Field>
      </div>
    </InlineForm>
  );
}

function RescheduleForm({
  workItemId,
  currentDueAt,
  externallyCommitted,
  onCancel,
  onDone,
}: {
  workItemId: string;
  currentDueAt: string;
  externallyCommitted: boolean;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [dueAt, setDueAt] = useState(toDateTimeInput(currentDueAt));
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.patch(`/work-items/${workItemId}`, {
        commitment: { customerDueAt: new Date(dueAt).toISOString() },
        reason: reason || undefined,
      }),
    onSuccess: onDone,
  });

  const needsReason = externallyCommitted && toDateTimeInput(currentDueAt) !== dueAt;

  return (
    <InlineForm
      title="Change the customer date"
      onCancel={onCancel}
      onSubmit={() => mutation.mutate()}
      pending={mutation.isPending}
      error={mutation.error}
      submitLabel="Save"
      disabled={needsReason && reason.trim().length === 0}
    >
      <Field label="Customer due date" required>
        <Input
          type="datetime-local"
          value={dueAt}
          onChange={(event) => setDueAt(event.target.value)}
        />
      </Field>
      {needsReason && (
        <Callout tone="warning" title="This date was promised to the customer">
          Moving it needs a reason. The change and the reason are both recorded.
        </Callout>
      )}
      <Field label="Reason" required={needsReason}>
        <Textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={2} />
      </Field>
      <p className="text-xs text-muted">
        The internal target and readiness-check dates are re-derived from the new customer date.
      </p>
    </InlineForm>
  );
}

/** A form rendered inside the dialog body, below the detail. */
export function InlineForm({
  title,
  children,
  onCancel,
  onSubmit,
  pending,
  error,
  submitLabel,
  disabled,
}: {
  title: string;
  children: ReactNode;
  onCancel: () => void;
  onSubmit: () => void;
  pending?: boolean;
  error?: unknown;
  submitLabel: string;
  disabled?: boolean;
}) {
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      className="space-y-3 rounded-lg border border-accent/40 bg-accent-soft/40 p-3"
    >
      <h3 className="text-xs font-semibold text-ink">{title}</h3>
      {children}
      {error instanceof ApiError && (
        <p className="text-xs text-critical">{error.message}</p>
      )}
      {Boolean(error) && !(error instanceof ApiError) && (
        <p className="text-xs text-critical">
          {error instanceof Error ? error.message : 'Something went wrong.'}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" variant="primary" disabled={pending || disabled}>
          {pending ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}
